import { useState } from "react";
import { ChevronDown, ShieldCheck } from "lucide-react";
import { Eyebrow, SectionHeading, track } from "./_shared";

const TRUST_PILLS = [
  "HIPAA-aligned", "BAA on every plan", "AES-256 at rest", "TLS 1.3",
  "Audit logs", "Tamper-evident", "SOC 2 in progress",
];

const FAQS = [
  { q: "How does per-active-client pricing work?",
    a: "An active client is one with at least one logged visit in a calendar month. Caregivers, admins, and family members are always unlimited and never count toward billing. If a client has zero visits in a month, they're not billed for that month." },
  { q: "Does Afyanex meet U.S. EVV requirements?",
    a: "Yes. We capture GPS check-in/check-out with a geofence around each client's home address, and export to Sandata or HHAeXchange CSV. Auto-SFTP submission is on the roadmap." },
  { q: "What does switching look like?",
    a: "Most agencies go live in one afternoon. Import clients via CSV or quick-add, invite your team with one-click invite links, and you're documenting visits the same day. We help with white-glove onboarding on Scale." },
  { q: "Do you sign a BAA?",
    a: "Yes — every plan, every customer. The BAA is auto-signed in-app on first login. Sub-processors (Anthropic, OpenAI, MongoDB Atlas) are all under their own BAAs with us." },
  { q: "Can I try it before talking to anyone?",
    a: "Absolutely. Create an agency account in 60 seconds — no card, no sales call, 14-day free trial with the full feature set." },
];

function FAQItem({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-clinical-border last:border-0" data-testid={`landing-faq-${idx}`}>
      <button
        type="button"
        onClick={() =>
          setOpen((v) => {
            if (!v) track("landing_faq_opened", { faq_index: idx, question: q });
            return !v;
          })
        }
        className="flex w-full items-center justify-between gap-4 py-5 text-left group"
      >
        <span className="font-heading font-medium text-clinical-text group-hover:text-afyanex-blue transition-colors">{q}</span>
        <ChevronDown className={`h-4 w-4 text-clinical-subtext shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <p className="pb-5 -mt-1 text-[15px] leading-relaxed text-clinical-subtext">{a}</p>
      )}
    </div>
  );
}

export default function LandingTrustAndFAQ() {
  return (
    <>
      {/* Trust pills */}
      <section className="bg-white border-y border-clinical-border/60">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 py-16 text-center">
          <Eyebrow>Trust by design</Eyebrow>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-clinical-text">
            Audit-ready, from day one.
          </h2>
          <p className="mt-4 text-clinical-subtext max-w-2xl mx-auto">
            Built for the standards modern home-care agencies are held to.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-2">
            {TRUST_PILLS.map((t) => (
              <span key={t}
                    className="inline-flex items-center gap-1.5 rounded-full bg-clinical-bg border border-clinical-border/70 px-3.5 py-1.5 text-[12px] font-medium text-clinical-text">
                <ShieldCheck className="h-3 w-3 text-afyanex-blue" /> {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-3xl px-5 sm:px-8 py-24">
        <SectionHeading eyebrow="Frequently asked">
          Questions agencies ask <span className="bg-gradient-to-r from-afyanex-blue to-afyanex-pink bg-clip-text text-transparent">before they start.</span>
        </SectionHeading>
        <div className="rounded-2xl bg-white border border-clinical-border/70 px-6">
          {FAQS.map((f, i) => <FAQItem key={i} idx={i} q={f.q} a={f.a} />)}
        </div>
      </section>
    </>
  );
}
